import {
  context,
  trace,
  TraceFlags,
  SpanStatusCode,
  propagation,
} from '@opentelemetry/api';
import type { Span, Tracer } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ExportResultCode,
  W3CTraceContextPropagator,
  CompositePropagator,
} from '@opentelemetry/core';
import type { ExportResult } from '@opentelemetry/core';
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  SimpleSpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import type {
  ReadableSpan,
  SpanExporter,
  SpanProcessor,
} from '@opentelemetry/sdk-trace-base';
import {
  WebTracerProvider,
  StackContextManager,
} from '@opentelemetry/sdk-trace-web';
import {
  registerInstrumentations,
  InstrumentationBase,
} from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import Raven from 'raven-js';
import enviro from 'enviro';
import { getFullUrl } from 'hubspot-url-utils';
import { na1 } from 'hubspot-url-utils/hublets';
import { OtlpExporter, LOCAL_OTLP_ENDPOINT } from './otlpExporter';
import type { TracingOptions } from 'opentelemetry-fe-types';
import { HsTraceIdRatioBasedSampler } from './HsTraceIdRatioBasedSampler';
import { getElementSelector, getNearestComponentName } from './elementSelector';
import { instrumentQuickFetch } from '../integrations/quickfetch';
import { WebSocketInstrumentation } from '../integrations/websocket';
import { instrumentSpaNavigation } from '../quartz/routing';
import { DevConsoleSpanExporter } from './DevConsoleSpanExporter';

const LOCAL_OTLP_FLAG = 'OTEL_LOCAL_EXPORTER';
const PUBLIC_TELEMETRY_PATH = '/hs-open-telemetry-public/v1/public';

// https://philipwalton.com/articles/idle-until-urgent/
const isSafari = () =>
  !!(
    typeof (window as any).safari === 'object' &&
    (window as any).safari.pushNotification
  );

function getPublicBaseUrl(hublet: string): string {
  const apiUrl = getFullUrl('api');
  // getFullUrl('api') returns 'https://api.hubspot.com' for na1 but
  // 'https://api-eu1.hubspot.com' for other hublets, so use a regex
  // that strips the entire 'api(-hublet)?' subdomain.
  return apiUrl.replace(/\/\/api(?:-[^.]+)?\./, `//metrics-fe-${hublet}.`);
}

async function fetchPublicSampleRate(
  serviceName: string
): Promise<number | null> {
  const url = `${getPublicBaseUrl(
    na1
  )}${PUBLIC_TELEMETRY_PATH}/sample-ratios?serviceName=${encodeURIComponent(
    serviceName
  )}&hublet=${encodeURIComponent(
    enviro.deployed() ? enviro.getHublet() ?? na1 : na1
  )}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = (await res.json()) as { sampleRatio?: number };
  return data.sampleRatio ?? null;
}

class NoopSpanExporter implements SpanExporter {
  export(
    _spans: ReadableSpan[],
    resultCallback: (result: ExportResult) => void
  ): void {
    resultCallback({ code: ExportResultCode.SUCCESS });
  }
  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

interface OtelFeState {
  provider: WebTracerProvider | null;
  initializing: boolean;
  serviceName: string | null;
  isSampled: boolean | null;
  exportResults: ExportResult[];
  exportFailureReported: boolean;
  unloadListener: (() => void) | null;
  listenTo: string | null;
  wsInstrumentation: WebSocketInstrumentation | null;
  spaNavUnlisten: (() => void) | null;
}

const GLOBAL_STATE_KEY = '__otelFe_1';
const _state: OtelFeState = ((window as unknown as Record<string, OtelFeState>)[
  GLOBAL_STATE_KEY
] ??= {
  provider: null,
  initializing: false,
  serviceName: null,
  isSampled: null,
  exportResults: [],
  exportFailureReported: false,
  unloadListener: null,
  listenTo: null,
  wsInstrumentation: null,
  spaNavUnlisten: null,
});

let _testExporter: SpanExporter | null = null;
const _readyCallbacks: Array<() => void> = [];

export async function initTracing(
  serviceName: string,
  {
    enabled,
    sampleRate = 0.01,
    dynamicSampleRate = true,
    instrumentDocumentLoad = true,
    instrumentFetch = false,
    instrumentUserInteraction = true,
    userInteractionElementSelector,
    instrumentXhr = false,
    instrumentQuickFetch: instrumentQuickFetchOption = false,
    instrumentWebSocket = false,
    instrumentSpaNavigation: instrumentSpaNavigationOption = false,
    propagateTraceHeaderCorsUrls,
    propagateTraceHeaderWebSocketUrls,
    ignoreUrls,
    applyCustomAttributesOnSpan,
    httpClient,
    endpoint,
    additionalSpanProcessors,
  }: TracingOptions = {}
): Promise<void> {
  if (_state.provider !== null || _state.initializing) {
    console.warn(
      `[opentelemetry-fe] initTracing() was already called. Ignoring duplicate call with serviceName "${serviceName}".`
    );
    return;
  }
  _state.initializing = true;

  if (instrumentQuickFetchOption) {
    instrumentQuickFetch();
  }

  try {
    const isEnabled = enabled ?? true;

    if (dynamicSampleRate && isEnabled) {
      try {
        const fetched = await fetchPublicSampleRate(serviceName);
        if (fetched !== null) {
          sampleRate = fetched;
        } else {
          console.warn(
            `[opentelemetry-fe] No sample ratio returned for "${serviceName}"; using default ${sampleRate}.`
          );
        }
      } catch (err) {
        console.warn(
          `[opentelemetry-fe] Failed to fetch sample rate for "${serviceName}"; using default ${sampleRate}. Error: ${String(
            err
          )}`
        );
      }
    }

    if (applyCustomAttributesOnSpan && !instrumentXhr) {
      console.warn(
        '[opentelemetry-fe] applyCustomAttributesOnSpan was provided but instrumentXhr is not enabled; the hook will not be called.'
      );
    }

    let exporter: SpanExporter;
    let exporterUrl: string | null = null;
    if (!isEnabled) {
      exporter = new NoopSpanExporter();
    } else {
      let flag: string | null = null;
      try {
        flag = localStorage.getItem(LOCAL_OTLP_FLAG);
      } catch (_e) {
        // localStorage may be unavailable (e.g. private browsing with strict settings)
      }

      if (flag === 'otlp') {
        exporterUrl = LOCAL_OTLP_ENDPOINT;
        exporter = new OtlpExporter();
      } else if (flag === 'tempo' || enviro.deployed()) {
        const resolvedEndpoint =
          endpoint ??
          `${getPublicBaseUrl(
            enviro.deployed() ? enviro.getHublet() ?? na1 : na1
          )}${PUBLIC_TELEMETRY_PATH}/traces`;

        exporterUrl = resolvedEndpoint;
        exporter = new OtlpExporter(resolvedEndpoint, {
          httpClient,
        });
      } else if (flag === 'console') {
        exporter = new DevConsoleSpanExporter();
      } else {
        if (flag !== null) {
          console.warn(
            `[opentelemetry-fe] Unrecognized OTEL_LOCAL_EXPORTER value: "${flag}". No spans will be exported.`
          );
        }
        exporter = new NoopSpanExporter();
      }
    }

    const allIgnoreUrls: (string | RegExp)[] = [
      ...(exporterUrl
        ? [new RegExp(exporterUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))]
        : []),
      ...(ignoreUrls ?? []),
    ];

    const trackedExporter: SpanExporter = {
      export(
        spans: ReadableSpan[],
        resultCallback: (result: ExportResult) => void
      ): void {
        exporter.export(spans, (result: ExportResult) => {
          _state.exportResults.push(result);
          if (
            result.code === ExportResultCode.FAILED &&
            !_state.exportFailureReported
          ) {
            _state.exportFailureReported = true;
            Raven.captureMessage('[opentelemetry-fe] Span export failed', {
              level: 'info',
              extra: { error: result.error?.message },
              tags: { library_name: 'opentelemetry-fe' },
            });
          }
          resultCallback(result);
        });
      },
      shutdown(): Promise<void> {
        return exporter.shutdown();
      },
    };

    const spanProcessor = _testExporter
      ? new SimpleSpanProcessor(_testExporter)
      : new BatchSpanProcessor(trackedExporter);

    const validatedSampleRate = (() => {
      if (Number.isNaN(sampleRate)) {
        console.warn(
          '[opentelemetry-fe] sampleRate is NaN; falling back to default (0.01).'
        );
        return 0.01;
      }
      if (sampleRate < 0 || sampleRate > 1) {
        const clamped = Math.min(1, Math.max(0, sampleRate));
        console.warn(
          `[opentelemetry-fe] sampleRate ${sampleRate} is out of range [0.0, 1.0]; clamping to ${clamped}.`
        );
        return clamped;
      }
      return sampleRate;
    })();

    const sampler = new ParentBasedSampler({
      root: new HsTraceIdRatioBasedSampler(validatedSampleRate),
    });

    const sessionId = (window as any).hubspot?.sessionId;

    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({
        [ATTR_SERVICE_NAME]: serviceName,
        ...(sessionId != null && { 'hubspot.session_id': String(sessionId) }),
      }),
      sampler,
      spanProcessors: [spanProcessor, ...(additionalSpanProcessors ?? [])],
    });

    const contextManager = new StackContextManager();
    contextManager.enable();

    const shouldInstrument =
      instrumentDocumentLoad ||
      instrumentFetch ||
      instrumentUserInteraction ||
      instrumentXhr ||
      instrumentWebSocket;

    provider.register({
      contextManager,
      propagator: isEnabled
        ? new W3CTraceContextPropagator()
        : new CompositePropagator(),
    });

    if (shouldInstrument) {
      const instrumentations: InstrumentationBase[] = [];
      if (instrumentDocumentLoad) {
        instrumentations.push(new DocumentLoadInstrumentation());
      }
      if (instrumentFetch) {
        instrumentations.push(
          new FetchInstrumentation({
            propagateTraceHeaderCorsUrls,
            ignoreUrls: allIgnoreUrls,
          })
        );
      }
      if (instrumentUserInteraction) {
        // Workaround for a bug in @opentelemetry/instrumentation-user-interaction@0.65.0:
        // addPatchedListener stores event targets and listeners as WeakMap keys without guarding
        // against non-object values, causing "Invalid value used as weak map key" crashes.
        // A bare addEventListener(...) call (no `window.`) reaches the patched function with
        // `this` undefined, which the native method treats as the global object, so we key
        // those registrations on window. Returning false would make the patched function skip
        // the native addEventListener and silently drop the listener.
        // The methods are typed private so we can't subclass — patch the instance directly.
        // Remove this patch once upstream ships a fix.
        const uiInstr = new UserInteractionInstrumentation({
          shouldPreventSpanCreation: (
            _eventType: string,
            element: HTMLElement,
            span: Span
          ) => {
            const selectorFn =
              userInteractionElementSelector ?? getElementSelector;
            const selector = selectorFn(element);
            if (selector) {
              span.setAttribute('ui.element.selector', selector);
            }
            const componentName = getNearestComponentName(element);
            if (componentName) {
              span.setAttribute('ui.component.name', componentName);
            }
          },
        });
        const origAddPatchedListener = (uiInstr as any).addPatchedListener.bind(
          uiInstr
        );
        const origRemovePatchedListener = (
          uiInstr as any
        ).removePatchedListener.bind(uiInstr);
        const resolveTarget = (on: unknown) => on ?? window;
        (uiInstr as any).addPatchedListener = (
          on: unknown,
          type: string,
          listener: unknown,
          wrappedListener: unknown
        ): boolean => {
          const target = resolveTarget(on);
          if (typeof target !== 'object' && typeof target !== 'function') {
            return false;
          }
          if (
            listener == null ||
            (typeof listener !== 'object' && typeof listener !== 'function')
          ) {
            return false;
          }
          return origAddPatchedListener(
            target,
            type,
            listener,
            wrappedListener
          );
        };
        (uiInstr as any).removePatchedListener = (
          on: unknown,
          type: string,
          listener: unknown
        ) => origRemovePatchedListener(resolveTarget(on), type, listener);
        instrumentations.push(uiInstr);
      }
      if (instrumentXhr) {
        instrumentations.push(
          new XMLHttpRequestInstrumentation({
            propagateTraceHeaderCorsUrls,
            ignoreUrls: allIgnoreUrls,
            applyCustomAttributesOnSpan,
          })
        );
      }
      if (instrumentWebSocket) {
        const wsInstr = new WebSocketInstrumentation({
          ignoreUrls: allIgnoreUrls,
          propagateTraceHeaderWebSocketUrls,
        });
        _state.wsInstrumentation = wsInstr;
        instrumentations.push(wsInstr);
      }
      registerInstrumentations({
        instrumentations,
        tracerProvider: provider,
      });
    }

    const probeSpan = trace.getTracer(serviceName).startSpan('otel-init');
    _state.isSampled =
      probeSpan.spanContext().traceFlags === TraceFlags.SAMPLED;
    probeSpan.end();

    _state.provider = provider;
    _state.serviceName = serviceName;

    if (instrumentSpaNavigationOption) {
      _state.spaNavUnlisten = instrumentSpaNavigation();
    }

    for (const cb of _readyCallbacks.splice(0)) {
      cb();
    }

    const listenTo = isSafari() ? 'beforeunload' : 'visibilitychange';
    const flushOnUnload = () => {
      if (
        document.visibilityState === 'hidden' ||
        listenTo === 'beforeunload'
      ) {
        // The browser cancels the in-flight export request when the page is
        // hidden or unloaded, so forceFlush() routinely rejects here (with an
        // array of errors, per BasicTracerProvider). Catch so it does not
        // surface as an unhandled promise rejection.
        provider.forceFlush().catch((e: unknown) => {
          Raven.captureException(e as Error);
        });
        if (listenTo === 'beforeunload') {
          try {
            window.removeEventListener(listenTo, flushOnUnload, true);
          } catch (_e) {
            // ignore
          }
          _state.unloadListener = null;
        }
      }
    };
    _state.listenTo = listenTo;
    _state.unloadListener = flushOnUnload;
    try {
      window.addEventListener(listenTo, flushOnUnload, true);
    } catch (_e) {
      // ignore
    }
  } finally {
    _state.initializing = false;
  }
}

export function isSampled(): boolean | null {
  return _state.isSampled;
}

export function isTracingInitializing(): boolean {
  return _state.initializing;
}

export function onTracingReady(callback: () => void): void {
  if (_state.provider !== null) {
    callback();
  } else {
    _readyCallbacks.push(callback);
  }
}

export function addTracingSpanProcessor(processor: SpanProcessor): void {
  const doAdd = () => {
    if (_state.provider === null) return;
    // opentelemetry-web/static-1.8 does not expose addSpanProcessor publicly;
    // access the internal MultiSpanProcessor._spanProcessors array directly.
    const multi = (_state.provider as unknown as Record<string, unknown>)
      ._activeSpanProcessor as
      | { _spanProcessors?: SpanProcessor[] }
      | undefined;
    multi?._spanProcessors?.push(processor);
  };
  if (_state.provider !== null) {
    doAdd();
  } else {
    _readyCallbacks.push(doAdd);
  }
}

export function getTracer(name?: string): Tracer {
  if (_state.provider === null && !_state.initializing) {
    throw new Error(
      'OpenTelemetry tracing has not been initialized. Call initTracing(serviceName) before using getTracer(). Use getSafeTracer() if you cannot guarantee initTracing() has been called.'
    );
  }
  return getSafeTracer(name);
}

/**
 * Like getTracer(), but never throws. Returns a no-op tracer if tracing has
 * not been initialized (or is still initializing) instead of raising an
 * error. Intended for library code that emits spans but can't control or
 * verify whether the host app has called initTracing().
 */
export function getSafeTracer(name?: string): Tracer {
  if (_state.provider !== null && _state.serviceName !== null) {
    return trace.getTracer(name ?? _state.serviceName);
  }
  // Tracing is either still initializing (e.g. awaiting the dynamic
  // sample-rate fetch) or was never initialized. Either way, no global OTel
  // provider is registered, so trace.getTracer() returns a no-op tracer —
  // spans created from it are silently dropped rather than throwing.
  return trace.getTracer(name ?? 'opentelemetry-fe-pending');
}

export async function flushTracing(): Promise<{
  success: boolean;
  error?: string;
}> {
  if (_state.provider === null) {
    throw new Error(
      'OpenTelemetry tracing has not been initialized. Call initTracing(serviceName) before using flushTracing().'
    );
  }
  _state.exportResults = [];
  try {
    await _state.provider.forceFlush();
  } catch (e) {
    Raven.captureException(e as Error);
  }
  if (_state.exportResults.length === 0) {
    return { success: false, error: 'No spans were exported' };
  }
  const failed = _state.exportResults.find(
    (r) => r.code === ExportResultCode.FAILED
  );
  return {
    success: !failed,
    error: failed?.error?.message,
  };
}

export async function _initTracingForTesting(
  serviceName: string,
  options: TracingOptions,
  exporter: SpanExporter
): Promise<void> {
  _testExporter = exporter;
  await initTracing(serviceName, options);
}

export function _setTestExporterForTesting(exporter: SpanExporter): void {
  _testExporter = exporter;
}

export function _resetForTesting(): void {
  if (_state.spaNavUnlisten !== null) {
    _state.spaNavUnlisten();
    _state.spaNavUnlisten = null;
  }
  if (_state.wsInstrumentation !== null) {
    _state.wsInstrumentation.disable();
    _state.wsInstrumentation = null;
  }
  if (_state.provider !== null) {
    _state.provider.shutdown().catch(() => {});
  }
  if (_state.unloadListener !== null && _state.listenTo !== null) {
    try {
      window.removeEventListener(_state.listenTo, _state.unloadListener, true);
    } catch (_e) {
      // ignore
    }
  }
  propagation.disable();
  context.disable();
  trace.disable();
  delete (window as unknown as Record<string, unknown>)[GLOBAL_STATE_KEY];
  _state.provider = null;
  _state.initializing = false;
  _state.serviceName = null;
  _state.isSampled = null;
  _state.spaNavUnlisten = null;
  _state.exportResults = [];
  _state.exportFailureReported = false;
  _state.unloadListener = null;
  _state.listenTo = null;
  _testExporter = null;
  _readyCallbacks.length = 0;
}

export { context, trace, propagation, SpanStatusCode };
export type { Span, Tracer };
